import { normalizeWorkerIdString } from '../../utils/workerAgentIds';
import { normalizeOptimizationDatasetNames, parseDefaultsDatasetConfig } from '../../utils/optimizationDatasetName';
import { mergeConfiguratorModelOptions } from '../../utils/configuratorModels';
import type { StateCreator } from 'zustand';
import type { OptimizationState } from '../optimizationStore';
import {
  WorkerAgentConfig,
  AdditionalWorkerConfig,
  MultiAgentDefaults,
  ChatMessage,
  cloneWorkers,
  cloneAdditionalWorkerConfig,
  areWorkersEqual,
  areAdditionalWorkerConfigsEqual,
  DEFAULT_COORDINATOR_DESCRIPTION,
  INITIAL_WORKERS,
  INITIAL_ADDITIONAL_WORKER_CONFIG,
} from '../storeShared';

/** Multi-agent optimization page: coordinator, workers, defaults, run and chat. (AIT-423) */
export interface MultiAgentSlice {
  multiAgentIterations: number; // Отдельное поле для мультиагентных итераций
  // Мультиагентные настройки
  workers: WorkerAgentConfig[];
  coordinatorPrompt: string;
  coordinatorModel: string;
  coordinatorTemperature: number;
  coordinatorDescription: string;
  coordinatorReminderPrompt: string;
  /** Display name for coordinator in multi-agent UI (from defaults / API). */
  coordinatorName: string;

  // Конфигурация для дополнительных worker агентов (3-й и далее)
  additionalWorkerConfig: AdditionalWorkerConfig;

  multiAgentDefaults: MultiAgentDefaults;

  // Мультиагентные результаты оптимизации
  multiAgentOptimizedPrompts: string[];
  multiAgentIterationResults: Array<{
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    iteration: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
    temperature?: number;
    'master-agent'?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    'worker-agents'?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      mode?: 'synchronous' | 'asynchronous';
      reminder_prompt?: string;
    }>;
    meta_prompts?: {
      CORE_PROMPT?: string;
      MASTER_SUFFIX?: string;
      ASYNC_SUFFIX?: string;
      SYNC_EXTRA?: string;
      RAG_PROMPT?: string;
      LTM_PROMPT?: string;
      MODERATION_PROMPT?: string;
      DIARY_WRITE_PROMPT?: string;
      DIARY_READ_PROMPT?: string;
      PORTRAIT_READ_PROMPT?: string;
      PORTRAIT_WRITE_PROMPT?: string;
      FINAL_STEP_PROMPT?: string;
    };
    metaPrompts?: {
      CORE_PROMPT?: string;
      MASTER_SUFFIX?: string;
      ASYNC_SUFFIX?: string;
      SYNC_EXTRA?: string;
      RAG_PROMPT?: string;
      LTM_PROMPT?: string;
      MODERATION_PROMPT?: string;
      DIARY_WRITE_PROMPT?: string;
      DIARY_READ_PROMPT?: string;
      PORTRAIT_READ_PROMPT?: string;
      PORTRAIT_WRITE_PROMPT?: string;
      FINAL_STEP_PROMPT?: string;
    };
  }>;
  multiAgentIsOptimized: boolean;
  multiAgentSelectedIterationIndex: number;
  multiAgentSelectedPrompt: string;
  multiAgentSelectedPromptIndex: string;
  multiAgentGraphSliderValue: number;
  multiAgentIsOptimizing: boolean;
  multiAgentOptimizationProgress: number;

  // Экспоненциальный прогресс-бар для multi-agent
  multiAgentProgressStartTime: number | null;
  multiAgentCurrentProgressStage: number;
  multiAgentExpectedIterationTime: number;

  // Чат для Multi-Agent
  multiAgentChatMessages: {
    before: ChatMessage[];
    after: ChatMessage[];
  };
  multiAgentChatInputs: {
    before: string;
    after: string;
  };

  setMultiAgentIterations: (iterations: number) => void; // Новое действие для мультиагентных итераций
  setWorkers: (workers: WorkerAgentConfig[]) => void;
  setCoordinatorPrompt: (prompt: string) => void;
  setCoordinatorModel: (model: string) => void;
  setCoordinatorTemperature: (temperature: number) => void;
  setCoordinatorDescription: (description: string) => void;
  setCoordinatorReminderPrompt: (value: string) => void;
  setCoordinatorName: (name: string) => void;

  // Мультиагентные действия
  setMultiAgentOptimizedPrompts: (prompts: string[]) => void;
  setMultiAgentIterationResults: (
    results: Array<{
      prompt: string;
      embedding_point?: { x: number; y: number };
      evaluation_point?: { x: number; y: number };
      iteration: number;
      seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
      temperature?: number;
      'master-agent'?: {
        name: string;
        prompt: string;
        temperature: number;
        model_name: string;
        description: string;
        reminder_prompt?: string;
      };
      'worker-agents'?: Array<{
        name: string;
        prompt: string;
        temperature: number;
        model_name: string;
        description: string;
        mode?: 'synchronous' | 'asynchronous';
        reminder_prompt?: string;
      }>;
    }>
  ) => void;
  setMultiAgentIsOptimized: (isOptimized: boolean) => void;
  setMultiAgentSelectedIterationIndex: (index: number) => void;
  setMultiAgentSelectedPrompt: (prompt: string) => void;
  setMultiAgentSelectedPromptIndex: (index: string) => void;
  setMultiAgentGraphSliderValue: (value: number) => void;
  setMultiAgentIsOptimizing: (isOptimizing: boolean) => void;
  setMultiAgentOptimizationProgress: (progress: number) => void;
  setMultiAgentProgressStartTime: (time: number | null) => void;
  setMultiAgentCurrentProgressStage: (stage: number) => void;
  setMultiAgentExpectedIterationTime: (time: number) => void;

  addMultiAgentIterationResult: (result: {
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    iteration: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
    temperature?: number;
    'master-agent'?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    'worker-agents'?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    }>;
  }) => void;
  processMultiAgentOptimizationResult: (result: {
    prompt?: string;
    prompts?: string[];
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    totalIterations?: number;
    temperature?: number;
    'master-agent'?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    'worker-agents'?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    }>;
  }) => void;
  // Функция для загрузки конфигурации по умолчанию
  loadDefaults: () => Promise<void>;

  // Функция для сброса настроек к дефолтам
  resetToDefaults: () => Promise<void>;

  // Действия для работы с чатами Multi-Agent
  setMultiAgentChatMessages: (messages: { before: ChatMessage[]; after: ChatMessage[] }) => void;
  setMultiAgentChatInputs: (inputs: { before: string; after: string }) => void;
  setMultiAgentChatInput: (type: 'before' | 'after', value: string) => void;
  addMultiAgentChatMessage: (type: 'before' | 'after', message: ChatMessage) => void;
  clearMultiAgentChat: () => void;
  isMultiAgentAtDefaults: () => boolean;
}

export const createMultiAgentSlice: StateCreator<
  OptimizationState,
  [['zustand/persist', unknown]],
  [],
  MultiAgentSlice
> = (set, get) => ({
  multiAgentIterations: 4, // Значение по умолчанию для мультиагентных итераций
  workers: cloneWorkers(INITIAL_WORKERS),
  coordinatorPrompt: 'You are a helpful assistant.',
  coordinatorModel: 'gpt-4.1-mini',
  coordinatorTemperature: 0.7,
  coordinatorDescription: DEFAULT_COORDINATOR_DESCRIPTION,
  coordinatorReminderPrompt: '',
  coordinatorName: 'Coordinator',

  // Конфигурация для дополнительных worker агентов по умолчанию
  additionalWorkerConfig: cloneAdditionalWorkerConfig(INITIAL_ADDITIONAL_WORKER_CONFIG),

  multiAgentDefaults: {
    selectedDataset: '',
    multiAgentIterations: 4,
    coordinatorPrompt: 'You are a helpful assistant.',
    coordinatorModel: 'gpt-4.1-mini',
    coordinatorTemperature: 0.7,
    coordinatorDescription: DEFAULT_COORDINATOR_DESCRIPTION,
    coordinatorReminderPrompt: '',
    coordinatorName: 'Coordinator',
    workers: cloneWorkers(INITIAL_WORKERS),
    additionalWorkerConfig: cloneAdditionalWorkerConfig(INITIAL_ADDITIONAL_WORKER_CONFIG),
  },

  // Мультиагентные результаты оптимизации
  multiAgentOptimizedPrompts: [],
  multiAgentIterationResults: [],
  multiAgentIsOptimized: false,
  multiAgentSelectedIterationIndex: 0,
  multiAgentSelectedPrompt: '',
  multiAgentSelectedPromptIndex: '',
  multiAgentGraphSliderValue: 0,
  multiAgentIsOptimizing: false,
  multiAgentOptimizationProgress: 0,

  // Экспоненциальный прогресс-бар для multi-agent
  multiAgentProgressStartTime: null,
  multiAgentCurrentProgressStage: 0,
  multiAgentExpectedIterationTime: 60,

  // Начальные значения для чатов Multi-Agent
  multiAgentChatMessages: {
    before: [],
    after: [],
  },
  multiAgentChatInputs: {
    before: '',
    after: '',
  },

  setMultiAgentIterations: (iterations: number) => {
    const numIterations = Number(iterations);
    const currentState = get();
    const maxIterations = currentState.iterationsMax || 10;

    // Убеждаемся что значение в допустимых пределах
    const validIterations = Math.max(1, Math.min(numIterations, maxIterations));

    set({ multiAgentIterations: validIterations });
  },
  setWorkers: (workers: WorkerAgentConfig[]) =>
    set({
      workers: cloneWorkers(workers.map((w, i) => ({ ...w, id: normalizeWorkerIdString(w.id, i) }))),
    }),
  setCoordinatorPrompt: (prompt: string) => set({ coordinatorPrompt: prompt }),
  setCoordinatorModel: (model: string) => set({ coordinatorModel: model }),
  setCoordinatorTemperature: (temperature: number) => set({ coordinatorTemperature: Math.min(temperature, 1) }),
  setCoordinatorDescription: (description: string) => set({ coordinatorDescription: description }),
  setCoordinatorReminderPrompt: (value: string) => set({ coordinatorReminderPrompt: value }),
  setCoordinatorName: (name: string) => set({ coordinatorName: name.trim() || 'Coordinator' }),

  isMultiAgentAtDefaults: () => {
    const state = get();
    const defaults = state.multiAgentDefaults;

    if (!defaults) {
      return true;
    }

    const datasetMatches = (state.selectedDataset || '') === (defaults.selectedDataset || '');
    const iterationsMatch = state.multiAgentIterations === defaults.multiAgentIterations;
    const coordinatorMatches =
      state.coordinatorPrompt === defaults.coordinatorPrompt &&
      state.coordinatorModel === defaults.coordinatorModel &&
      state.coordinatorTemperature === defaults.coordinatorTemperature &&
      state.coordinatorDescription === defaults.coordinatorDescription &&
      (state.coordinatorReminderPrompt || '') === (defaults.coordinatorReminderPrompt || '') &&
      (state.coordinatorName || 'Coordinator') === (defaults.coordinatorName || 'Coordinator');
    const additionalMatches = areAdditionalWorkerConfigsEqual(
      state.additionalWorkerConfig,
      defaults.additionalWorkerConfig
    );
    const workersMatch = areWorkersEqual(state.workers, defaults.workers);

    return datasetMatches && iterationsMatch && coordinatorMatches && additionalMatches && workersMatch;
  },

  // Мультиагентные действия
  setMultiAgentOptimizedPrompts: (prompts: string[]) => set({ multiAgentOptimizedPrompts: prompts }),
  setMultiAgentIterationResults: (results) => set({ multiAgentIterationResults: results }),
  setMultiAgentIsOptimized: (isOptimized: boolean) => set({ multiAgentIsOptimized: isOptimized }),
  setMultiAgentSelectedIterationIndex: (index: number) => {
    console.log(`🔄 setMultiAgentSelectedIterationIndex called with: ${index}`);
    set({ multiAgentSelectedIterationIndex: index });
  },
  setMultiAgentSelectedPrompt: (prompt: string) => set({ multiAgentSelectedPrompt: prompt }),
  setMultiAgentSelectedPromptIndex: (index: string) => set({ multiAgentSelectedPromptIndex: index }),
  setMultiAgentGraphSliderValue: (value: number) => set({ multiAgentGraphSliderValue: value }),
  setMultiAgentIsOptimizing: (isOptimizing: boolean) => set({ multiAgentIsOptimizing: isOptimizing }),
  setMultiAgentOptimizationProgress: (progress: number) =>
    set((state: OptimizationState) => {
      const incoming = Math.max(0, Math.min(100, progress));
      const current =
        typeof state.multiAgentOptimizationProgress === 'number' ? state.multiAgentOptimizationProgress : 0;

      // ✅ Разрешаем обнуление прогресса (для повторного запуска оптимизации)
      // Если incoming === 0, значит это начало новой оптимизации - принудительно обнуляем
      const next = incoming === 0 ? 0 : Math.max(current, incoming);

      console.log('📊 setMultiAgentOptimizationProgress:', { current, incoming, next });

      if (next !== current) {
        return { multiAgentOptimizationProgress: next };
      }
      return {};
    }),
  setMultiAgentProgressStartTime: (time: number | null) => set({ multiAgentProgressStartTime: time }),
  setMultiAgentCurrentProgressStage: (stage: number) => set({ multiAgentCurrentProgressStage: stage }),
  setMultiAgentExpectedIterationTime: (time: number) => set({ multiAgentExpectedIterationTime: time }),
  addMultiAgentIterationResult: (result) =>
    set((state: OptimizationState) => {
      console.log('➕ addMultiAgentIterationResult called with:', result);

      const seq = typeof result.seq === 'number' ? result.seq : undefined;
      const incomingSeqOrIter = seq !== undefined ? seq : result.iteration;

      // Deduplicate by seq/iteration: if this step already exists, do not append again
      if (typeof incomingSeqOrIter === 'number') {
        const alreadyExists = state.multiAgentIterationResults.some((r) => {
          const existingSeqOrIter = typeof r.seq === 'number' ? r.seq : r.iteration;
          return existingSeqOrIter === incomingSeqOrIter;
        });
        if (alreadyExists) {
          console.log(`♻️ Skipping duplicate multi-agent result for seq=${incomingSeqOrIter}`);
          return {};
        }
      }

      // seq: 0 - это baseline evaluation, добавляем только в iterationResults, но НЕ в optimizedPrompts
      if (seq === 0) {
        console.log(
          '📊 Multi-agent seq: 0 - baseline evaluation, adding to iterationResults only (not to optimizedPrompts)'
        );

        // Создаем маппинг для замены имен агентов на кастомные имена
        const agentNameMapping: { [key: string]: string } = {};
        agentNameMapping['master'] = 'Coordinator';

        if (result['worker-agents'] && state.workers) {
          result['worker-agents'].forEach((apiAgent: any, index: number) => {
            const customAgent = state.workers[index];
            if (customAgent && apiAgent.name) {
              agentNameMapping[apiAgent.name] = customAgent.name;
              agentNameMapping[`worker${index + 1}`] = customAgent.name;
              agentNameMapping[`worker-${index + 1}`] = customAgent.name;
            }
          });
        }

        const updatedResult = {
          ...result,
          seq: 0,
          iteration: 0,
          'master-agent': result['master-agent']
            ? {
                ...result['master-agent'],
                name: agentNameMapping[result['master-agent'].name] || result['master-agent'].name,
              }
            : undefined,
          // Устанавливаем mode: 'synchronous' для всех worker-agents до получения seq 0
          'worker-agents': result['worker-agents']?.map((agent: any) => ({
            ...agent,
            name: agentNameMapping[agent.name] || agent.name,
            mode: 'synchronous', // Все агенты в synchronous до получения seq 0
          })),
        };

        return {
          multiAgentIterationResults: [...state.multiAgentIterationResults, updatedResult],
        };
      }

      // seq: 1, 2, 3... - это оптимизированные конфигурации
      // Создаем маппинг для замены имен агентов на кастомные имена
      const agentNameMapping: { [key: string]: string } = {};

      // Маппинг для master-agent
      agentNameMapping['master'] = 'Coordinator';

      // Маппинг для worker агентов
      if (result['worker-agents'] && state.workers) {
        result['worker-agents'].forEach((apiAgent: any, index: number) => {
          const customAgent = state.workers[index];
          if (customAgent && apiAgent.name) {
            agentNameMapping[apiAgent.name] = customAgent.name;
            agentNameMapping[`worker${index + 1}`] = customAgent.name;
            agentNameMapping[`worker-${index + 1}`] = customAgent.name;
          }
        });
      }

      // Обновляем результат с кастомными именами
      const updatedResult = {
        ...result,
        seq: seq !== undefined ? seq : result.iteration,
        iteration: seq !== undefined ? seq : result.iteration,
        'master-agent': result['master-agent']
          ? {
              ...result['master-agent'],
              name: agentNameMapping[result['master-agent'].name] || result['master-agent'].name,
            }
          : undefined,
        'worker-agents': result['worker-agents']?.map((agent: any) => ({
          ...agent,
          name: agentNameMapping[agent.name] || agent.name,
        })),
      };

      console.log('🔄 Updated result with custom names:', updatedResult);

      // Добавляем промпт в optimizedPrompts только для seq > 0
      const masterPrompt = updatedResult['master-agent']?.prompt || updatedResult.prompt;
      const currentOptimizedCount = state.multiAgentOptimizedPrompts.length;
      const maxIterations = state.multiAgentIterations || 4;

      // Проверяем лимит оптимизированных промптов
      if (currentOptimizedCount < maxIterations && masterPrompt) {
        const updatedPrompts = [...state.multiAgentOptimizedPrompts, masterPrompt];

        const newResults = [...state.multiAgentIterationResults, updatedResult];
        console.log('📊 Updated multiAgentIterationResults:', newResults);
        return {
          multiAgentIterationResults: newResults,
          multiAgentOptimizedPrompts: updatedPrompts,
        };
      }

      const newResults = [...state.multiAgentIterationResults, updatedResult];
      console.log('📊 Updated multiAgentIterationResults:', newResults);
      return {
        multiAgentIterationResults: newResults,
      };
    }),
  processMultiAgentOptimizationResult: (result) =>
    set((state: OptimizationState) => {
      console.log('🔄 processMultiAgentOptimizationResult called with:', result);

      // Фильтруем промпты: исключаем seq: 0 (baseline evaluation)
      let updatedPrompts = state.multiAgentOptimizedPrompts;
      if (result.prompts && result.prompts.length > 0) {
        // Фильтруем промпты, исключая seq: 0
        updatedPrompts = result.prompts;
        console.log('📝 Updated prompts from result.prompts:', updatedPrompts);
      } else if (result.prompt) {
        // Проверяем, что это не seq: 0 (baseline evaluation)
        const lastResult = state.multiAgentIterationResults[state.multiAgentIterationResults.length - 1];
        if (lastResult && lastResult.seq !== 0) {
          updatedPrompts = [...state.multiAgentOptimizedPrompts, result.prompt];
          console.log('📝 Added single prompt to existing prompts:', updatedPrompts);
        } else {
          console.log('📝 Skipping prompt addition - seq: 0 (baseline evaluation)');
        }
      }

      // НЕ добавляем новый результат в iterationResults, так как промежуточные результаты уже добавлены
      // Просто обновляем промпты и флаг оптимизации
      return {
        multiAgentOptimizedPrompts: updatedPrompts,
        multiAgentIsOptimized: true,
      };
    }),
  // Действия для работы с чатами Multi-Agent
  setMultiAgentChatMessages: (messages: { before: ChatMessage[]; after: ChatMessage[] }) =>
    set({ multiAgentChatMessages: messages }),
  setMultiAgentChatInputs: (inputs: { before: string; after: string }) => set({ multiAgentChatInputs: inputs }),
  setMultiAgentChatInput: (type: 'before' | 'after', value: string) =>
    set((state) => ({
      multiAgentChatInputs: {
        ...state.multiAgentChatInputs,
        [type]: value,
      },
    })),
  addMultiAgentChatMessage: (type: 'before' | 'after', message: ChatMessage) =>
    set((state) => ({
      multiAgentChatMessages: {
        ...state.multiAgentChatMessages,
        [type]: [...state.multiAgentChatMessages[type], message],
      },
    })),
  clearMultiAgentChat: () =>
    set({
      multiAgentChatMessages: { before: [], after: [] },
      multiAgentChatInputs: { before: '', after: '' },
    }),

  // Функция для загрузки конфигурации по умолчанию
  loadDefaults: async () => {
    try {
      const { autoTune } = await import('../../services/api');
      const defaults = await autoTune.getDefaults();

      console.log('🌐 Store: Raw API response:', defaults);
      console.log('🌐 Store: API data structure:', JSON.stringify(defaults.data, null, 2));

      // Новая структура ответа с endpoint
      const modelOptions = mergeConfiguratorModelOptions(defaults.data?.models?.options || ['gpt-4.1-mini']);
      const defaultModel = defaults.data?.models?.default || 'gpt-4.1-mini';
      const { options: datasetOptions, defaultName: defaultDataset } = parseDefaultsDatasetConfig(
        defaults.data?.datasets
      );
      const defaultSelectedDatasets = defaultDataset ? [defaultDataset] : [];
      const defaultTemperature = defaults.data?.temperature?.default;
      const safeTemperature = isNaN(Number(defaultTemperature)) ? 0.7 : Number(defaultTemperature);
      const defaultIterations = defaults.data?.iterations?.default;
      const maxIterations = defaults.data?.iterations?.max || 10;
      const safeIterations = isNaN(Number(defaultIterations))
        ? 4
        : Math.max(1, Math.min(Number(defaultIterations), maxIterations));
      const defaultPrompt = defaults.data?.default_prompt || 'You are a helpful assistant.';

      // Обработка мульти-агентных дефолтов
      const masterAgent = defaults.data?.['master-agent'];
      const workerAgents = defaults.data?.['worker-agents'];
      const additionalWorker = defaults.data?.['additional-worker'];

      console.log('🤖 Store: Extracted master-agent:', masterAgent);
      console.log('🤖 Store: Extracted worker-agents:', workerAgents);
      console.log('🤖 Store: Extracted additional-worker:', additionalWorker);

      let coordinatorPrompt = defaultPrompt;
      let coordinatorModel = defaultModel;
      let coordinatorTemperature = safeTemperature;
      let coordinatorDescription = DEFAULT_COORDINATOR_DESCRIPTION;
      let coordinatorReminderPrompt = '';
      let coordinatorName = 'Coordinator';

      // Конфигурация для дополнительных worker агентов
      let additionalWorkerConfig = {
        name: 'Assistant',
        prompt: 'You are a helpful assistant',
        model: defaultModel,
        temperature: safeTemperature,
        description: 'General-purpose assistant',
      };

      let workers: WorkerAgentConfig[] = [
        {
          id: '1',
          name: 'Expert',
          prompt: defaultPrompt,
          model: defaultModel,
          temperature: safeTemperature,
          description: `AI agent that follows this prompt:\n${defaultPrompt}`,
        },
        {
          id: '2',
          name: 'Synthesizer',
          prompt: defaultPrompt,
          model: defaultModel,
          temperature: safeTemperature,
          description: `AI agent that follows this prompt:\n${defaultPrompt}`,
        },
      ];

      if (masterAgent || workerAgents) {
        console.log('🤖 Store: Processing multi-agent defaults:', { masterAgent, workerAgents });

        // Обрабатываем master agent
        if (masterAgent) {
          coordinatorPrompt = masterAgent.prompt || defaultPrompt;
          coordinatorModel = masterAgent.model_name || defaultModel;
          coordinatorTemperature = masterAgent.temperature || safeTemperature;
          coordinatorDescription =
            masterAgent.description ||
            `Coordinator node that communicates with worker nodes to respond to user messages.`;
          coordinatorReminderPrompt =
            typeof masterAgent.reminder_prompt === 'string' ? masterAgent.reminder_prompt : '';
          coordinatorName =
            typeof masterAgent.name === 'string' && masterAgent.name.trim() ? masterAgent.name.trim() : 'Coordinator';
        }

        // Обрабатываем worker agents
        if (workerAgents && workerAgents.length > 0) {
          workers = workerAgents.map((worker: any, index: number) => ({
            id: normalizeWorkerIdString(worker?.id, index),
            name: worker.name || `Worker ${index + 1}`,
            prompt: worker.prompt || defaultPrompt,
            model: worker.model_name || defaultModel,
            temperature: worker.temperature || safeTemperature,
            description: worker.description || `AI agent that follows this prompt:\n${worker.prompt || defaultPrompt}`,
            reminder_prompt: typeof worker.reminder_prompt === 'string' ? worker.reminder_prompt : undefined,
          }));
        }
      }

      // Обрабатываем конфигурацию additional-worker
      if (additionalWorker) {
        console.log('🤖 Store: Processing additional-worker config:', additionalWorker);
        additionalWorkerConfig = {
          name: additionalWorker.name || 'Assistant',
          prompt: additionalWorker.prompt || 'You are a helpful assistant',
          model: additionalWorker.model_name || defaultModel,
          temperature: additionalWorker.temperature || safeTemperature,
          description: additionalWorker.description || 'General-purpose assistant',
        };
      }

      console.log('🔧 Store: Parsed values:', {
        modelOptions,
        defaultModel,
        datasetOptions,
        defaultDataset,
        safeTemperature,
        safeIterations,
        defaultPrompt,
        masterAgent: masterAgent ? 'present' : 'not present',
        workerAgents: workerAgents ? `${workerAgents.length} agents` : 'not present',
        coordinatorPrompt,
        coordinatorModel,
        coordinatorTemperature,
        workersCount: workers.length,
      });

      const defaultsSnapshot: MultiAgentDefaults = {
        selectedDataset: defaultDataset || '',
        multiAgentIterations: safeIterations,
        coordinatorPrompt,
        coordinatorModel,
        coordinatorTemperature,
        coordinatorDescription,
        coordinatorReminderPrompt,
        coordinatorName,
        workers: cloneWorkers(workers),
        additionalWorkerConfig: cloneAdditionalWorkerConfig(additionalWorkerConfig),
      };

      const currentState = get();

      // Проверяем, есть ли уже сохраненные пользовательские настройки
      // Если дефолты еще не загружались, всегда загружаем их
      // Если уже загружались, проверяем наличие пользовательских изменений
      const hasUserSettings =
        currentState.isDefaultsLoaded &&
        (currentState.selectedModel !== '' || currentState.coordinatorPrompt !== '' || currentState.workers.length > 0);

      const resolveSelectedDatasets = (): string[] => {
        if (!hasUserSettings) return defaultSelectedDatasets;
        const fromList = normalizeOptimizationDatasetNames(currentState.selectedDatasets).filter((d) =>
          datasetOptions.includes(d)
        );
        if (fromList.length > 0) return fromList;
        const legacy = currentState.selectedDataset?.trim();
        if (legacy && datasetOptions.includes(legacy)) return [legacy];
        return defaultSelectedDatasets;
      };
      const nextSelectedDatasets = resolveSelectedDatasets();

      // Обновляем только доступные опции и дефолты, если нет пользовательских настроек
      set({
        // Всегда обновляем доступные опции
        availableModels: modelOptions,
        availableDatasets: datasetOptions,
        iterationsMax: maxIterations,

        // Обновляем только если нет пользовательских настроек
        selectedModel: hasUserSettings ? currentState.selectedModel : defaultModel,
        temperature: hasUserSettings ? currentState.temperature : safeTemperature,
        iterations: hasUserSettings ? currentState.iterations : safeIterations,
        multiAgentIterations: hasUserSettings ? currentState.multiAgentIterations : safeIterations,
        selectedDataset: hasUserSettings
          ? nextSelectedDatasets[0] || currentState.selectedDataset
          : defaultDataset || '',
        selectedDatasets: nextSelectedDatasets,

        // Промпты - используем сохраненные или дефолтные
        initialPrompt: currentState.initialPrompt || defaultPrompt,
        botPrompt: currentState.botPrompt || defaultPrompt,

        // Мультиагентные настройки - используем сохраненные или дефолтные
        coordinatorPrompt: hasUserSettings ? currentState.coordinatorPrompt : coordinatorPrompt,
        coordinatorModel: hasUserSettings ? currentState.coordinatorModel : coordinatorModel,
        coordinatorTemperature: hasUserSettings ? currentState.coordinatorTemperature : coordinatorTemperature,
        coordinatorDescription: hasUserSettings ? currentState.coordinatorDescription : coordinatorDescription,
        coordinatorReminderPrompt: hasUserSettings ? currentState.coordinatorReminderPrompt : coordinatorReminderPrompt,
        coordinatorName: hasUserSettings ? currentState.coordinatorName : coordinatorName,
        workers: hasUserSettings ? currentState.workers : cloneWorkers(workers),
        additionalWorkerConfig: hasUserSettings
          ? currentState.additionalWorkerConfig
          : cloneAdditionalWorkerConfig(additionalWorkerConfig),
        multiAgentDefaults: defaultsSnapshot,

        // Устанавливаем флаг, что дефолты загружены
        isDefaultsLoaded: true,
      });

      console.log('🔄 loadDefaults set iterations to:', hasUserSettings ? currentState.iterations : safeIterations);
      console.log('🔄 loadDefaults set temperature to:', hasUserSettings ? currentState.temperature : safeTemperature);
      console.log('🔄 loadDefaults set default prompt to:', defaultPrompt);
      console.log('🔄 loadDefaults set coordinatorPrompt to:', coordinatorPrompt);
      console.log('🔄 loadDefaults set coordinatorModel to:', coordinatorModel);
      console.log('🔄 loadDefaults set coordinatorTemperature to:', coordinatorTemperature);
      console.log('🔄 loadDefaults set coordinatorDescription to:', coordinatorDescription);
      console.log('🔄 loadDefaults set workers to:', workers);

      console.log('✅ Loaded defaults from backend:', defaults);

      // Проверяем финальное состояние store
      const finalState = get();
      console.log('🎯 Store: Final state after loadDefaults:', {
        coordinatorPrompt: finalState.coordinatorPrompt,
        coordinatorModel: finalState.coordinatorModel,
        coordinatorTemperature: finalState.coordinatorTemperature,
        workers: finalState.workers,
        availableModels: finalState.availableModels,
        availableDatasets: finalState.availableDatasets,
      });
    } catch (error) {
      console.error('❌ Failed to load defaults:', error);
      // Используем значения по умолчанию при ошибке
      const fallbackWorkers = cloneWorkers(INITIAL_WORKERS);
      const fallbackAdditionalWorker = cloneAdditionalWorkerConfig(INITIAL_ADDITIONAL_WORKER_CONFIG);

      set({
        selectedModel: 'gpt-4.1-mini',
        temperature: 0.7,
        iterations: 6,
        multiAgentIterations: 4,
        availableModels: mergeConfiguratorModelOptions(['gpt-4.1-mini']),
        availableDatasets: [],
        selectedDataset: '',
        iterationsMax: 10,
        initialPrompt: 'You are a helpful assistant.',
        botPrompt: 'You are a helpful assistant.',
        // Мультиагентные fallback значения
        coordinatorPrompt: 'You are a helpful assistant.',
        coordinatorModel: 'gpt-4.1-mini',
        coordinatorTemperature: 0.7,
        coordinatorDescription: DEFAULT_COORDINATOR_DESCRIPTION,
        coordinatorReminderPrompt: '',
        coordinatorName: 'Coordinator',
        workers: fallbackWorkers,
        additionalWorkerConfig: fallbackAdditionalWorker,
        multiAgentDefaults: {
          selectedDataset: '',
          multiAgentIterations: 4,
          coordinatorPrompt: 'You are a helpful assistant.',
          coordinatorModel: 'gpt-4.1-mini',
          coordinatorTemperature: 0.7,
          coordinatorDescription: DEFAULT_COORDINATOR_DESCRIPTION,
          coordinatorReminderPrompt: '',
          coordinatorName: 'Coordinator',
          workers: cloneWorkers(fallbackWorkers),
          additionalWorkerConfig: cloneAdditionalWorkerConfig(fallbackAdditionalWorker),
        },
      });
    }
  },

  // Функция для сброса настроек к дефолтам
  resetToDefaults: async () => {
    try {
      const { autoTune } = await import('../../services/api');
      const defaults = await autoTune.getDefaults();

      console.log('🔄 Resetting to defaults from API:', defaults);

      // Извлекаем дефолтные значения
      const modelOptions = mergeConfiguratorModelOptions(defaults.data?.models?.options || ['gpt-4.1-mini']);
      const defaultModel = defaults.data?.models?.default || 'gpt-4.1-mini';
      const { options: datasetOptions, defaultName: defaultDataset } = parseDefaultsDatasetConfig(
        defaults.data?.datasets
      );
      const defaultSelectedDatasets = defaultDataset ? [defaultDataset] : [];
      const defaultTemperature = defaults.data?.temperature?.default;
      const safeTemperature = isNaN(Number(defaultTemperature)) ? 0.7 : Number(defaultTemperature);
      const defaultIterations = defaults.data?.iterations?.default;
      const maxIterations = defaults.data?.iterations?.max || 10;
      const safeIterations = isNaN(Number(defaultIterations))
        ? 4
        : Math.max(1, Math.min(Number(defaultIterations), maxIterations));
      const defaultPrompt = defaults.data?.default_prompt || 'You are a helpful assistant.';

      // Обработка мульти-агентных дефолтов
      const masterAgent = defaults.data?.['master-agent'];
      const workerAgents = defaults.data?.['worker-agents'];
      const additionalWorker = defaults.data?.['additional-worker'];

      let coordinatorPrompt = defaultPrompt;
      let coordinatorModel = defaultModel;
      let coordinatorTemperature = safeTemperature;
      let coordinatorDescription = DEFAULT_COORDINATOR_DESCRIPTION;
      let coordinatorReminderPrompt = '';
      let coordinatorName = 'Coordinator';

      let additionalWorkerConfig = {
        name: 'Assistant',
        prompt: 'You are a helpful assistant',
        model: defaultModel,
        temperature: safeTemperature,
        description: 'General-purpose assistant',
      };

      let workers: WorkerAgentConfig[] = [
        {
          id: '1',
          name: 'Expert',
          prompt: defaultPrompt,
          model: defaultModel,
          temperature: safeTemperature,
          description: `AI agent that follows this prompt:\n${defaultPrompt}`,
        },
        {
          id: '2',
          name: 'Synthesizer',
          prompt: defaultPrompt,
          model: defaultModel,
          temperature: safeTemperature,
          description: `AI agent that follows this prompt:\n${defaultPrompt}`,
        },
      ];

      if (masterAgent || workerAgents) {
        if (masterAgent) {
          coordinatorPrompt = masterAgent.prompt || defaultPrompt;
          coordinatorModel = masterAgent.model_name || defaultModel;
          coordinatorTemperature = masterAgent.temperature || safeTemperature;
          coordinatorDescription =
            masterAgent.description ||
            `Coordinator node that communicates with worker nodes to respond to user messages.`;
          coordinatorReminderPrompt =
            typeof masterAgent.reminder_prompt === 'string' ? masterAgent.reminder_prompt : '';
          coordinatorName =
            typeof masterAgent.name === 'string' && masterAgent.name.trim() ? masterAgent.name.trim() : 'Coordinator';
        }

        if (workerAgents && workerAgents.length > 0) {
          workers = workerAgents.map((worker: any, index: number) => ({
            id: normalizeWorkerIdString(worker?.id, index),
            name: worker.name || `Worker ${index + 1}`,
            prompt: worker.prompt || defaultPrompt,
            model: worker.model_name || defaultModel,
            temperature: worker.temperature || safeTemperature,
            description: worker.description || `AI agent that follows this prompt:\n${worker.prompt || defaultPrompt}`,
            reminder_prompt: typeof worker.reminder_prompt === 'string' ? worker.reminder_prompt : undefined,
          }));
        }
      }

      if (additionalWorker) {
        additionalWorkerConfig = {
          name: additionalWorker.name || 'Assistant',
          prompt: additionalWorker.prompt || 'You are a helpful assistant',
          model: additionalWorker.model_name || defaultModel,
          temperature: additionalWorker.temperature || safeTemperature,
          description: additionalWorker.description || 'General-purpose assistant',
        };
      }

      // Принудительно сбрасываем все настройки к дефолтам
      const defaultsSnapshot: MultiAgentDefaults = {
        selectedDataset: defaultDataset || '',
        multiAgentIterations: safeIterations,
        coordinatorPrompt,
        coordinatorModel,
        coordinatorTemperature,
        coordinatorDescription,
        coordinatorReminderPrompt,
        coordinatorName,
        workers: cloneWorkers(workers),
        additionalWorkerConfig: cloneAdditionalWorkerConfig(additionalWorkerConfig),
      };

      set({
        selectedModel: defaultModel,
        temperature: safeTemperature,
        iterations: safeIterations,
        multiAgentIterations: safeIterations,
        availableModels: modelOptions,
        availableDatasets: datasetOptions,
        selectedDataset: defaultDataset || '',
        selectedDatasets: defaultSelectedDatasets,
        iterationsMax: maxIterations,
        initialPrompt: defaultPrompt,
        botPrompt: defaultPrompt,
        coordinatorPrompt,
        coordinatorModel,
        coordinatorTemperature,
        coordinatorDescription,
        coordinatorReminderPrompt,
        coordinatorName,
        workers: cloneWorkers(workers),
        additionalWorkerConfig: cloneAdditionalWorkerConfig(additionalWorkerConfig),
        multiAgentDefaults: defaultsSnapshot,
      });

      console.log('✅ Settings reset to defaults successfully');
    } catch (error) {
      console.error('❌ Failed to reset to defaults:', error);
    }
  },
});
